import {
  clearAuth as clearAuthState,
  isTokenExpired,
  logoutAndRedirect,
  notifySessionExpired,
  notifySessionWarning,
  resetLogoutRedirectGuard,
  scheduleAutoLogout,
  scheduleAutoLogoutFromStorage,
} from '../auth/session';
import { recordFailedFetch, recordRequestId } from '../betaHelp';
import { notifyConnectivityIssue } from './connectivityNotifier';

// In production, the API base should be provided via env var.
// CRA only exposes env vars prefixed with REACT_APP_.
const ENV_API_BASE = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE;

// Safe local-dev fallback only. Do NOT default to the current host in production (would hit the frontend domain).
const DEFAULT_API_BASE = 'http://localhost:8000';

export const API_BASE = (ENV_API_BASE || DEFAULT_API_BASE).replace(/\/+$/, '');

function shouldHandleAuthError(path, status) {
  if (status !== 401) {
    return false;
  }
  const normalized = String(path || '').split('?')[0];
  return normalized !== '/auth/login';
}

function logoutWithReason(reason) {
  logoutAndRedirect({ reason });
}

function handleProtectedUnauthorized(token) {
  if (isTokenExpired(token)) {
    notifySessionExpired();
    return;
  }
  logoutWithReason('unauthorized');
}

function shouldNotifyConnectivityIssue(status) {
  return status === 502 || status === 503 || status === 504;
}

function captureResponseRequestId(response) {
  const requestId = response?.headers?.get?.('X-Request-ID');
  if (requestId) {
    recordRequestId(requestId);
  }
}

export async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('authToken');
  const headers = new Headers(options.headers || {});
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const p = String(path || '');
  if (process.env.NODE_ENV !== 'production') {
    const body = options?.body;
    const isPartSubmitPath = p.includes('/part-attempts/attempts/') && p.includes('/submit');
    if (body instanceof FormData && isPartSubmitPath) {
      if (!body.has('input_type')) {
        throw new Error('Guard: missing input_type in multipart submit');
      }
      const hasPayload =
        body.has('files') || body.has('file') || body.has('image_url') || body.has('image_urls');
      if (!hasPayload) {
        throw new Error('Guard: missing image payload in multipart submit');
      }
    }
  }
  const url = `${API_BASE}${p.startsWith('/') ? p : `/${p}`}`;
  let res;
  try {
    res = await fetch(url, { ...options, headers });
  } catch (error) {
    recordFailedFetch(p, null, options.method);
    notifyConnectivityIssue();
    throw error;
  }
  captureResponseRequestId(res);
  if (!res.ok) {
    recordFailedFetch(p, res.status, options.method);
  }
  // Manual test: sign in, then force backend to return 401 on a protected endpoint;
  // app should clear auth and hard-redirect to /login?reason=unauthorized.
  if (shouldHandleAuthError(p, res.status)) {
    handleProtectedUnauthorized(token);
    return res;
  }
  if (shouldNotifyConnectivityIssue(res.status)) {
    notifyConnectivityIssue();
  }
  return res;
}

export async function parseJsonSafe(response) {
  if (!response) {
    return null;
  }
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
}

export function nowIso() {
  return new Date().toISOString();
}

export function getStoredUser() {
  const raw = localStorage.getItem('authUser');
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
}

export function getAuth() {
  return {
    token: localStorage.getItem('authToken'),
    user: getStoredUser(),
  };
}

export function setStoredUser(user) {
  localStorage.setItem('authUser', JSON.stringify(user ?? null));
}

export function setAuth(token, user) {
  resetLogoutRedirectGuard();
  localStorage.setItem('authToken', token);
  localStorage.setItem('authUser', JSON.stringify(user ?? null));
  scheduleAutoLogout(token, notifySessionExpired, { onWarning: notifySessionWarning });
}

export function clearAuth() {
  clearAuthState();
}

export function logout(reason = null) {
  logoutAndRedirect({ reason });
}

export function isAuthenticated() {
  return Boolean(localStorage.getItem('authToken'));
}

export function initializeAuthSession() {
  return scheduleAutoLogoutFromStorage(notifySessionExpired, notifySessionWarning);
}

export async function hydrateAuthUser() {
  const token = localStorage.getItem('authToken');
  if (!token) {
    return { token: null, user: null, hydrated: true };
  }

  const res = await apiFetch('/auth/me');
  if (res.status === 401) {
    // Hydration runs before AuthenticatedLayout (and its SessionExpiryGuard) mounts,
    // so an expiry event cannot preserve an already-mounted page here. Fail closed
    // and use the existing same-user route restore instead of leaving stale auth.
    if (isTokenExpired(token)) {
      logoutWithReason('expired');
    }
    return { token: null, user: null, hydrated: true, loggedOut: true };
  }
  if (!res.ok) {
    return { token, user: getStoredUser(), hydrated: true };
  }

  const me = await parseJsonSafe(res);
  setStoredUser(me);
  return { token, user: me, hydrated: true };
}

export async function getMyAccount() {
  const response = await apiFetch('/auth/me');
  const data = (await parseJsonSafe(response)) || {};
  if (!response.ok) {
    throw new Error(
      (typeof data?.detail === 'string' && data.detail)
      || 'Could not load your account. Please try again.'
    );
  }
  setStoredUser(data);
  return data;
}

export async function passwordResetConfirm(token, newPassword) {
  const response = await apiFetch('/auth/password-reset/confirm', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      token,
      new_password: newPassword,
    }),
  });

  const data = (await parseJsonSafe(response)) || {};

  if (!response.ok) {
    const message =
      (typeof data?.detail === 'string' && data.detail) ||
      (typeof data?.message === 'string' && data.message) ||
      'Could not reset password. The link may have expired.';
    throw new Error(message);
  }

  return data;
}

export async function inviteConfirm(token, newPassword, marketingConsent) {
  const response = await apiFetch('/auth/invite/confirm', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      token,
      new_password: newPassword,
      marketing_consent: marketingConsent,
    }),
  });

  const data = (await parseJsonSafe(response)) || {};

  if (!response.ok) {
    const message =
      (typeof data?.detail === 'string' && data.detail) ||
      (typeof data?.message === 'string' && data.message) ||
      'Could not accept invite. The link may have expired.';
    throw new Error(message);
  }

  return data;
}

export async function updateMarketingConsent(consent) {
  const response = await apiFetch('/me/marketing-consent', {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ consent }),
  });

  const data = (await parseJsonSafe(response)) || {};

  if (!response.ok) {
    const error = new Error(
      (typeof data?.detail === 'string' && data.detail)
      || (typeof data?.message === 'string' && data.message)
      || 'Could not update publication permission. Please try again.'
    );
    error.status = response.status;
    throw error;
  }

  const storedUser = getStoredUser();
  if (storedUser) {
    setStoredUser({ ...storedUser, marketing_consent: data.consent });
  }
  return data;
}

export async function passwordResetRequest(email) {
  const response = await apiFetch('/auth/password-reset/request', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  const data = await parseJsonSafe(response);

  if (!response.ok) {
    throw new Error('Could not request password reset.');
  }

  return data;
}
